import { Maximize2 } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useProject } from "@/features/project";

const DIMENSION_PRESETS = [
  { label: "Stories (9:16)", width: 1080, height: 1920 },
  { label: "Post (4:5)", width: 1080, height: 1350 },
  { label: "Square (1:1)", width: 1080, height: 1080 },
  { label: "Landscape (16:9)", width: 1920, height: 1080 },
];

export const DimensionsPanel: React.FC = () => {
  const { dimensions, updateProject } = useProject();
  const [width, setWidth] = useState(dimensions.width.toString());
  const [height, setHeight] = useState(dimensions.height.toString());

  const setters = { width: setWidth, height: setHeight };

  const handlePresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = DIMENSION_PRESETS.find((p) => p.label === e.target.value);
    if (selected) {
      updateProject({ dimensions: selected });
      setWidth(selected.width.toString());
      setHeight(selected.height.toString());
    }
  };

  const handleDimensionChange = (key: "width" | "height", value: string) => {
    setters[key](value);
    const numVal = Number.parseInt(value, 10);
    if (Number.isNaN(numVal)) return;
    updateProject({
      dimensions: {
        ...dimensions,
        [key]: numVal,
      },
    });
  };

  const currentPreset =
    DIMENSION_PRESETS.find((p) => p.width === dimensions.width && p.height === dimensions.height)
      ?.label || "Custom";

  return (
    <div className="w-full border border-slate-700/50 py-1.5 px-2 bg-slate-800/80 backdrop-blur rounded-sm">
      {/* Header with preset */}
      <div className="flex items-center gap-2.5 md:gap-2 mb-3 md:mb-2">
        <Maximize2 size={12} className="text-slate-500 ml-1" />
        <select
          value={currentPreset}
          onChange={handlePresetChange}
          className="flex-1 h-8 md:h-6 bg-transparent md:text-xs text-slate-300 focus:outline-none cursor-pointer hover:text-white"
        >
          {DIMENSION_PRESETS.map((p) => (
            <option key={p.label} value={p.label} className="bg-slate-900">
              {p.label}
            </option>
          ))}
          <option value="Custom" className="bg-slate-900">
            Custom
          </option>
        </select>
      </div>

      {/* Width × Height */}
      <div className="flex items-center gap-2 md:text-xs">
        <input
          type="number"
          value={width}
          onChange={(e) => handleDimensionChange("width", e.target.value)}
          className="flex-1 w-24 h-8 md:h-6 px-2 bg-slate-900/50 text-center text-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
        <span className="text-slate-600">×</span>
        <input
          type="number"
          value={height}
          onChange={(e) => handleDimensionChange("height", e.target.value)}
          className="flex-1 w-24 h-8 md:h-6 px-2 bg-slate-900/50 text-center text-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </div>
    </div>
  );
};
